import * as layout from '../../components/markLayout.module.css';
import { MarkButton } from '../../components/MarkControls';
import React, { useState } from 'react';
import P from '@hero/ui-kit/typography/P';
import Tag from '@hero/ui-kit/components/Tag';
import type { DripDiff, DripStep } from '../../types';
type Props = {
    diff: DripDiff;
    draftVersion: number;
    currentVersion: number;
};
const StepLabel: React.FC<{
    step: DripStep;
}> = ({ step }) => (
    <span>
        {step.id} ({step.type})
    </span>
);
const DiffPanel: React.FC<Props> = ({ diff, draftVersion, currentVersion }) => {
    const [expanded, setExpanded] = useState(false);
    const total = diff.added.length + diff.removed.length + diff.changed.length;
    if (total === 0) {
        return (
            <div
                style={{
                    backgroundColor: 'var(--mark-success-surface)',
                    marginBottom: '1.2rem'
                }}
                className={layout.panel}
            >
                <P
                    noDefaultMargin
                    size="small"
                    styles={{
                        color: 'var(--mark-success)'
                    }}
                >
                    Draft (v{draftVersion}) matches Current (v{currentVersion}) — no step changes.
                </P>
            </div>
        );
    }
    return (
        <div
            style={{
                marginBottom: '1.2rem'
            }}
            className={layout.panel}
        >
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}
            >
                <P noDefaultMargin strong>
                    Draft (v{draftVersion}) vs Current (v{currentVersion}):{' '}
                    {diff.added.length > 0 && `+${diff.added.length} added`}
                    {diff.added.length > 0 && (diff.changed.length > 0 || diff.removed.length > 0)
                        ? ', '
                        : ''}
                    {diff.changed.length > 0 && `${diff.changed.length} changed`}
                    {diff.changed.length > 0 && diff.removed.length > 0 ? ', ' : ''}
                    {diff.removed.length > 0 && `${diff.removed.length} removed`}
                </P>
                <MarkButton type="button" onClick={() => setExpanded(!expanded)}>
                    {expanded ? 'Collapse' : 'Expand'}
                </MarkButton>
            </div>
            {expanded && (
                <div
                    style={{
                        marginTop: '1rem'
                    }}
                >
                    {diff.added.length > 0 && (
                        <div
                            style={{
                                marginBottom: '0.8rem'
                            }}
                        >
                            <P
                                noDefaultMargin
                                strong
                                size="small"
                                styles={{
                                    color: 'var(--mark-success)'
                                }}
                            >
                                Added
                            </P>
                            {diff.added.map((s) => (
                                <div
                                    key={s.id}
                                    style={{
                                        padding: '0.4rem 0.8rem',
                                        backgroundColor: 'var(--mark-success-surface)',
                                        borderRadius: '0.2rem',
                                        marginTop: '0.2rem'
                                    }}
                                >
                                    <P noDefaultMargin size="small">
                                        <StepLabel step={s} />
                                    </P>
                                </div>
                            ))}
                        </div>
                    )}
                    {diff.removed.length > 0 && (
                        <div
                            style={{
                                marginBottom: '0.8rem'
                            }}
                        >
                            <P
                                noDefaultMargin
                                strong
                                size="small"
                                styles={{
                                    color: 'var(--mark-danger)'
                                }}
                            >
                                Removed
                            </P>
                            {diff.removed.map((s) => (
                                <div
                                    key={s.id}
                                    style={{
                                        padding: '0.4rem 0.8rem',
                                        backgroundColor: 'var(--mark-danger-surface)',
                                        borderRadius: '0.2rem',
                                        marginTop: '0.2rem'
                                    }}
                                >
                                    <P noDefaultMargin size="small">
                                        <StepLabel step={s} />
                                    </P>
                                </div>
                            ))}
                        </div>
                    )}
                    {diff.changed.length > 0 && (
                        <div>
                            <P
                                noDefaultMargin
                                strong
                                size="small"
                                styles={{
                                    color: 'var(--mark-warning)'
                                }}
                            >
                                Changed
                            </P>
                            {diff.changed.map((c) => (
                                <div
                                    key={c.after.id}
                                    style={{
                                        padding: '0.4rem 0.8rem',
                                        backgroundColor: 'var(--mark-warning-surface)',
                                        borderRadius: '0.2rem',
                                        marginTop: '0.2rem'
                                    }}
                                >
                                    <P noDefaultMargin size="small">
                                        <StepLabel step={c.after} /> —{' '}
                                        {c.changedKeys.map((k) => (
                                            <Tag key={k} type="warn" size="small">
                                                {k}
                                            </Tag>
                                        ))}
                                    </P>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
export default DiffPanel;
